import { useState } from 'react'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { Alert } from '@/shared/components/ui'

interface Invitation {
  id: string
  unit_name: string
  unit_slug: string
  role: string
  email: string
  invited_by_name: string | null
  invited_by_email: string | null
}

interface InvitationsStepProps {
  invitations: Invitation[]
  onAcceptInvitation?: (invitationId: string) => Promise<void>
  onSkipInvitations?: () => void
  // Backwards-compatible prop names
  onAccept?: (invitationId: string) => Promise<void>
  onSkip?: () => void
  isAccepting: boolean
}

export function InvitationsStep({
  invitations,
  onAcceptInvitation,
  onSkipInvitations,
  onAccept,
  onSkip,
  isAccepting
}: InvitationsStepProps) {
  const { t } = useTranslation()
  const [selectedInvitation, setSelectedInvitation] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const roleLabels: Record<string, string> = {
    admin: t('roles.admin'),
    manager: t('roles.manager'),
    operator: t('roles.operator'),
    viewer: t('roles.viewer'),
    member: t('roles.member')
  }

  const roleColors: Record<string, string> = {
    admin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    operator: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    viewer: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
    member: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
  }

  const handleAccept = async (invitationId: string) => {
    try {
      setError(null)
      setSelectedInvitation(invitationId)
      const fn = onAcceptInvitation ?? onAccept
      if (fn) await fn(invitationId)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('onboarding.invitations.acceptError'))
      setSelectedInvitation(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-10 h-10 text-blue-600 dark:text-blue-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76"
            />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          {t('onboarding.invitations.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('onboarding.invitations.subtitle')}
        </p>
      </div>

      {/* Error Message */}
      {error && <Alert variant="error">{error}</Alert>}

      {/* Invitations List */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8 space-y-4">
        {invitations.map((invitation) => (
          <div
            key={invitation.id}
            className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors"
          >
            {/* Workspace icon */}
            <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg text-slate-900 dark:text-white leading-tight">
                {invitation.unit_name || t('onboarding.invitations.unknownWorkspace')}
              </h3>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors[invitation.role] ?? roleColors.viewer}`}>
                  {roleLabels[invitation.role] || invitation.role}
                </span>
                {(invitation.invited_by_name || invitation.invited_by_email) && (
                  <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {t('onboarding.invitations.invitedBy')}{' '}
                    <span className="font-medium text-slate-700 dark:text-slate-300">
                      {invitation.invited_by_name || invitation.invited_by_email}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* Accept button */}
            <button
              onClick={() => handleAccept(invitation.id)}
              disabled={isAccepting || selectedInvitation !== null}
              className="flex-shrink-0 px-6 py-2.5 rounded-lg font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectedInvitation === invitation.id && isAccepting ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  {t('onboarding.invitations.accepting')}
                </span>
              ) : (
                t('onboarding.invitations.accept')
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex flex-col items-center gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('onboarding.invitations.orCreateNew')}
        </p>
        <button
          onClick={onSkipInvitations ?? onSkip}
          disabled={isAccepting || selectedInvitation !== null}
          className="px-8 py-2 rounded-lg font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('onboarding.invitations.createNew')}
        </button>
      </div>
    </div>
  )
}
